import clsx from "clsx";
import { FC, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ArrowRight } from "@/assets/icons/icons.tsx";
import {
  Button,
  ButtonLink,
  getBtnClassName,
} from "@/components/Interface/Button/Button";
import { createIdentificationStatusQueryOptions } from "@/hooks/identificationStatusQueryOptions.ts";
import { useFWBMemberStatus } from "@/hooks/useFWBMemberStatus.ts";
import { useHasBillDunning } from "@/hooks/useHasBillDunning.ts";
import { useHasPendingMobileService } from "@/hooks/useHasPendingMobileService.ts";
import { useNeedsIDCheck } from "@/hooks/useNeedsIDCheck.ts";
import { portalRoutes } from "@/router/routes/portalRoutes.ts";
import { useGetIdentificationStatus } from "@/services/api.ts";
import { FCWithClassName } from "@/types";
import { getKycTodoConfig } from "@/utils/kycTodoConfig.ts";

interface TodoRowProps {
  label: string;
  detail?: string;
  cta?: React.ReactNode;
}

/** One task row: orange bullet, label with optional detail, and a CTA control. */
const TodoRow: FC<TodoRowProps> = ({ label, detail, cta }) => (
  <li className="flex items-center gap-3 rounded-lg bg-surface-tertiary py-2 pl-3 pr-2">
    <span
      className="size-2 shrink-0 rounded-full bg-surface-accent-orange"
      aria-hidden="true"
    />
    <div className="min-w-0 flex-1">
      <span className="text-body-sm font-semibold leading-md text-foreground-primary">
        {label}
      </span>
      {detail && (
        <p className="mt-1 text-body-xs font-normal text-foreground-secondary">
          {detail}
        </p>
      )}
    </div>
    {cta}
  </li>
);

/** Icon-only arrow button that navigates to an external URL. */
const ExternalArrowButton = ({
  href,
  label,
}: {
  href: string;
  label: string;
}) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    aria-label={label}
    className={clsx(
      getBtnClassName({ variant: "secondary", size: "sm" }),
      "btn-icon-only",
    )}
  >
    <ArrowRight />
  </a>
);

/**
 * Renders the "To-do" checklist: KYC identification, open bills, and FWB
 * mobile subscription prompts. Returns null when there are no actionable
 * items. Accepts `className` so callers can hide it at specific breakpoints
 * (e.g. `desktop:hidden` in the content column when the Sidebar shows it).
 */
export const CockpitTodosSection: FCWithClassName = ({ className }) => {
  const { t } = useTranslation("portal");
  // Dynamic keys from KycTodoConfig are valid at runtime but not statically typed — cast the function.
  const tKey = t as (key: string) => string;
  const navigate = useNavigate();

  const needsIdCheck = useNeedsIDCheck();
  const staleTime = useRef(0);
  const { data: identificationStatus } = useGetIdentificationStatus({
    query: createIdentificationStatusQueryOptions(staleTime),
  });

  const { isAtRiskOfRemoval } = useFWBMemberStatus();
  const hasBillDunning = useHasBillDunning();
  const hasPendingMobileService = useHasPendingMobileService();
  // `undefined` means "not known yet" - only show the todo once we can
  // confirm there's no pending mobile service, not while still loading.
  const showFwbMobileAboTodo =
    isAtRiskOfRemoval && hasPendingMobileService === false;

  const kycTodo =
    needsIdCheck === true && identificationStatus
      ? getKycTodoConfig(identificationStatus.kycStatus)
      : null;
  const showKycTodo = kycTodo !== null;
  const showTodosSection =
    showKycTodo || showFwbMobileAboTodo || hasBillDunning;

  if (!showTodosSection) {
    return null;
  }

  const todoCount = [showKycTodo, hasBillDunning, showFwbMobileAboTodo].filter(
    Boolean,
  ).length;

  return (
    <section
      data-testid="cockpit-todo-list"
      className={clsx(
        "flex flex-col gap-3 rounded-lg bg-surface-accent-ivory p-4",
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <span
          className="flex size-6 shrink-0 items-center justify-center rounded-full bg-surface-accent-orange text-body-xs font-semibold leading-none text-foreground-primary-inverse"
          aria-hidden="true"
        >
          {todoCount}
        </span>
        <h2 className="text-headline-sm font-semibold leading-md text-foreground-primary">
          {t("cockpit.dashboard.todos.title")}
        </h2>
      </div>

      <ul className="flex flex-col gap-2">
        {showKycTodo && kycTodo && (
          <TodoRow
            label={tKey(kycTodo.labelKey)}
            detail={kycTodo.detailKey ? tKey(kycTodo.detailKey) : undefined}
            cta={
              kycTodo.variant === "external" ? (
                <ExternalArrowButton
                  href={tKey(kycTodo.hrefKey!)}
                  label={tKey(kycTodo.labelKey)}
                />
              ) : kycTodo.variant === "internal" ? (
                <ButtonLink
                  to={kycTodo.href ?? portalRoutes.cockpit.idCheck}
                  variant="secondary"
                  size="sm"
                  icon={<ArrowRight />}
                  ariaLabel={tKey(kycTodo.labelKey)}
                />
              ) : undefined
            }
          />
        )}
        {hasBillDunning && (
          <TodoRow
            label={t("cockpit.dashboard.todos.payOpenBills")}
            cta={
              <ButtonLink
                to={portalRoutes.documents.bills}
                variant="secondary"
                size="sm"
                icon={<ArrowRight />}
                ariaLabel={t("cockpit.dashboard.todos.payOpenBills")}
              />
            }
          />
        )}
        {showFwbMobileAboTodo && (
          <TodoRow
            label={t("cockpit.dashboard.todos.fwbMobileAbo")}
            cta={
              <Button
                variant="secondary"
                size="sm"
                icon={<ArrowRight />}
                ariaLabel={t("cockpit.dashboard.todos.fwbMobileAbo")}
                onClick={() =>
                  navigate(portalRoutes.friendsWithBenefits.groupOverview)
                }
              />
            }
          />
        )}
      </ul>
    </section>
  );
};
